'use client';

import { useMemo, useState, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import {
  EventingProvider,
  FanOutAdapter,
  LinkedInAdapter,
  MetaPixelAdapter,
  createTagularAdapter,
  type EventingAdapter,
  type EventingContext,
  type FanOutAdapterErrorDetails,
} from '@highereducation/eventing';
import { newrelic } from '@hen-site/lib/newrelic/client';
import {
  henSiteInteractionRegistry,
  henSiteViewRegistry,
} from '@hen-site/lib/eventing/hen-site-registries';
import { readEventRequestContext } from '@hen-site/lib/eventing/readEventRequestContext';

export interface HenSiteEventingProviderProps {
  readonly children: ReactNode;
  readonly tenantId: string;
  readonly pixelId?: string;
  readonly partnerId?: string;
}

const resolveVendorId = (value?: string): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const toNoticeErrorAttributes = (
  details?: FanOutAdapterErrorDetails,
): Record<string, unknown> | undefined => {
  if (!details) {
    return undefined;
  }

  return Object.fromEntries(Object.entries(details));
};

const reportPaidSocialError = (
  error: unknown,
  details?: FanOutAdapterErrorDetails,
): void => {
  newrelic.noticeError?.(
    error instanceof Error
      ? error
      : new Error('PaidSocialAdapterFailed', { cause: error }),
    toNoticeErrorAttributes(details),
  );
};

export function HenSiteEventingProvider({
  children,
  tenantId,
  pixelId,
  partnerId,
}: HenSiteEventingProviderProps) {
  const pathname = usePathname();
  const [requestContext] = useState(readEventRequestContext);

  const adapter = useMemo(() => {
    const adapters: EventingAdapter[] = [createTagularAdapter()];
    const resolvedPixelId = resolveVendorId(pixelId);
    const resolvedPartnerId = resolveVendorId(partnerId);

    if (resolvedPixelId) {
      adapters.push(
        new MetaPixelAdapter({
          pixelId: resolvedPixelId,
          onError: reportPaidSocialError,
        }),
      );
    }
    if (resolvedPartnerId) {
      adapters.push(
        new LinkedInAdapter({
          partnerId: resolvedPartnerId,
          onError: reportPaidSocialError,
        }),
      );
    }

    return adapters.length === 1
      ? adapters[0]
      : new FanOutAdapter(adapters, { onError: reportPaidSocialError });
  }, [partnerId, pixelId]);

  const context = useMemo<EventingContext>(
    () => ({
      tenantId,
      pageUrl:
        typeof window !== 'undefined' && pathname
          ? `${window.location.origin}${pathname}`
          : pathname,
      experiment: requestContext.experiment,
      bot: requestContext.bot,
    }),
    [tenantId, pathname, requestContext],
  );

  return (
    <EventingProvider
      adapter={adapter}
      interactionRegistry={henSiteInteractionRegistry}
      viewRegistry={henSiteViewRegistry}
      context={context}
      registryPolicy={{ unknownComponent: 'ignore' }}
    >
      {children}
    </EventingProvider>
  );
}
